import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import axios from 'axios'
import { useTheme } from '../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface ExpansionPathway {
  seller: {
    StoreId: number
    StoreName: string | null
    CompanyName: string
    Email: string
    JoinDate: string
  }
  currentProfile: {
    markets: { Country: string; OrderCount: number; Revenue: number }[]
    channels: { Name: string; OrderCount: number; Revenue: number }[]
    productCategories: { CategoryName: string; ProductCount: number; AvgPrice: number }[]
    performance: {
      TotalOrders: number
      TotalRevenue: number
      AvgOrderValue: number
      MarketsServed: number
    }
  }
  recommendedMarkets: {
    country: string
    fitScore: number
    score: number
    size: string
    competition: string
    growth: number
    requirements: string[]
    timeline: string
    priority: string
  }[]
  expansionPathway: {
    overview: {
      sellerName: string
      currentMarketsCount: number
      currentChannelsCount: number
      totalRevenue: number
      readinessScore: number
    }
    phases: {
      phase: number
      name: string
      duration: string
      steps: {
        step: number
        title: string
        description: string
        tasks: string[]
        duration: string
        status: string
        aiInsight?: string
        marketData?: object
        requirements?: string[]
        estimatedCost?: string
      }[]
    }[]
    summary: {
      totalPhases: number
      totalSteps: number
      estimatedTimeline: string
      nextAction: string
    }
  }
  generatedAt: string
}

export const ExpansionPathwayPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const [showAiAnalysis, setShowAiAnalysis] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['expansion-pathway', storeId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/expansion-pathway/seller/${storeId}`)
      return data as ExpansionPathway
    },
    enabled: !!storeId,
  })

  const { data: aiAnalysis, isLoading: aiLoading } = useQuery({
    queryKey: ['expansion-ai-analysis', storeId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/expansion-pathway/seller/${storeId}/ai-recommendation`)
      return data
    },
    enabled: !!storeId && showAiAnalysis,
  })

  if (!storeId) {
    // Show list of sellers if no storeId
    return <ExpansionPathwayList />
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #7B68EE',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading expansion pathway...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading pathway: {(error as Error)?.message || 'Not found'}</p>
            <Button onClick={() => navigate('/expansion-pathway')} style={{ marginTop: '16px' }}>
              Back to Seller List
            </Button>
          </Card>
        </main>
      </div>
    )
  }

  const getStepStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#50C878'
      case 'in_progress': return '#4A90E2'
      case 'pending': return '#9CA3AF'
      case 'blocked': return '#EF4444'
      default: return '#9CA3AF'
    }
  }

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✓'
      case 'in_progress': return '●'
      case 'pending': return '○'
      case 'blocked': return '✕'
      default: return '○'
    }
  }

  // Extract data with safe defaults
  const sellerName = data.seller.StoreName || data.seller.CompanyName || 'Unknown Seller'
  const currentMarkets = data.currentProfile?.markets || []
  const performance = data.currentProfile?.performance || { TotalOrders: 0, TotalRevenue: 0, AvgOrderValue: 0, MarketsServed: 0 }
  const readinessScore = data.expansionPathway?.overview?.readinessScore || 0
  const recommendedMarkets = data.recommendedMarkets || []
  const phases = data.expansionPathway?.phases || []

  // Get primary country from first market
  const primaryCountry = currentMarkets.length > 0 ? currentMarkets[0].Country : 'Unknown'

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <button
                onClick={() => navigate('/expansion-pathway')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                ← Back to Sellers
              </button>
            </div>
            <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
              Expansion Pathway: {sellerName}
            </h1>
            <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
              Personalized expansion strategy based on your current performance and market opportunities
            </p>
          </div>
          <div style={{
            padding: '16px 24px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, #7B68EE20, #7B68EE40)`,
            border: '1px solid #7B68EE60',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '4px' }}>Readiness Score</p>
            <p style={{ fontSize: '36px', fontWeight: 700, color: '#7B68EE' }}>{readinessScore}%</p>
          </div>
        </div>

        {/* Seller Overview */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Current Status</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            <div>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Primary Market</p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{primaryCountry}</p>
            </div>
            <div>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Active Markets</p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{currentMarkets.length}</p>
            </div>
            <div>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Total Revenue</p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: '#50C878' }}>${(performance.TotalRevenue || 0).toLocaleString()}</p>
            </div>
            <div>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '4px' }}>Total Orders</p>
              <p style={{ fontSize: '18px', fontWeight: 600, color: colors.primary }}>{(performance.TotalOrders || 0).toLocaleString()}</p>
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Current Markets</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {currentMarkets.map(market => (
                <span key={market.Country} style={{
                  padding: '6px 12px',
                  backgroundColor: colors.primary + '20',
                  color: colors.primary,
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}>{market.Country} (${(market.Revenue || 0).toLocaleString()})</span>
              ))}
            </div>
          </div>
        </Card>

        {/* Recommended Markets */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Recommended Markets for Expansion</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {recommendedMarkets.slice(0, 6).map((market, idx) => (
              <div
                key={market.country}
                onClick={() => navigate(`/markets/${market.country}`)}
                style={{
                  padding: '20px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: idx === 0 ? `linear-gradient(135deg, #50C87810, #50C87820)` : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    {idx === 0 && (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: '#50C878',
                        color: '#FFFFFF',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        marginBottom: '8px',
                      }}>TOP PICK</span>
                    )}
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{market.country}</h3>
                  </div>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: `conic-gradient(#50C878 ${(market.fitScore || market.score || 0) * 3.6}deg, ${colors.border} 0deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: colors.cardBackground,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#50C878',
                    }}>{market.fitScore || market.score || 0}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
                  <span style={{ color: colors.textSecondary }}>Size: <strong style={{ color: colors.textPrimary }}>{market.size || 'N/A'}</strong></span>
                  <span style={{ color: colors.textSecondary }}>Competition: <strong style={{ color: colors.textPrimary }}>{market.competition || 'N/A'}</strong></span>
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                  <span>Growth: <strong style={{ color: '#50C878' }}>{market.growth || 0}%</strong></span>
                  <span style={{ marginLeft: '12px' }}>Timeline: <strong style={{ color: colors.textPrimary }}>{market.timeline || 'N/A'}</strong></span>
                </div>
                {market.requirements && market.requirements.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textSecondary }}>
                    Requirements: {market.requirements.slice(0, 2).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Expansion Phases */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '24px', color: colors.textPrimary }}>Your Expansion Pathway</h2>

          {phases.map((phase, phaseIdx) => (
            <div key={phase.phase} style={{ marginBottom: phaseIdx < phases.length - 1 ? '32px' : '0' }}>
              {/* Phase Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: colors.primary + '10',
                borderRadius: '8px',
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: colors.primary,
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                }}>
                  {phase.phase}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                    Phase {phase.phase}: {phase.name}
                  </h3>
                  <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
                    Duration: {phase.duration}
                  </p>
                </div>
              </div>

              {/* Phase Steps */}
              <div style={{ position: 'relative', paddingLeft: '20px' }}>
                {/* Progress Line */}
                <div style={{
                  position: 'absolute',
                  left: '20px',
                  top: '20px',
                  bottom: '20px',
                  width: '2px',
                  backgroundColor: colors.border,
                }} />

                {phase.steps.map((step, idx) => (
                  <div key={step.step} style={{
                    display: 'flex',
                    gap: '24px',
                    marginBottom: idx < phase.steps.length - 1 ? '24px' : '0',
                    position: 'relative',
                  }}>
                    {/* Step Indicator */}
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: colors.cardBackground,
                      border: `3px solid ${getStepStatusColor(step.status)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: getStepStatusColor(step.status),
                      flexShrink: 0,
                      zIndex: 1,
                    }}>
                      {getStepStatusIcon(step.status)}
                    </div>

                    {/* Step Content */}
                    <div style={{
                      flex: 1,
                      padding: '16px',
                      backgroundColor: step.status === 'in_progress' ? colors.primary + '08' : 'transparent',
                      border: `1px solid ${step.status === 'in_progress' ? colors.primary + '40' : colors.border}`,
                      borderRadius: '12px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            backgroundColor: getStepStatusColor(step.status) + '20',
                            color: getStepStatusColor(step.status),
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            marginRight: '8px',
                            textTransform: 'uppercase',
                          }}>{step.status.replace('_', ' ')}</span>
                          <h4 style={{ display: 'inline', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                            Step {step.step}: {step.title}
                          </h4>
                        </div>
                        <span style={{ fontSize: '12px', color: colors.textSecondary }}>{step.duration}</span>
                      </div>
                      <p style={{ color: colors.textSecondary, marginBottom: '12px', lineHeight: 1.6, fontSize: '14px' }}>{step.description}</p>

                      {step.tasks && step.tasks.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary, marginBottom: '6px' }}>Tasks:</p>
                          <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '13px' }}>
                            {step.tasks.map((task, i) => (
                              <li key={i} style={{ marginBottom: '4px' }}>{task}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.requirements && step.requirements.length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {step.requirements.map((req, i) => (
                            <span key={i} style={{
                              padding: '4px 10px',
                              backgroundColor: '#FF6B35' + '20',
                              color: '#FF6B35',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}>{req}</span>
                          ))}
                        </div>
                      )}

                      {step.aiInsight && (
                        <div style={{
                          marginTop: '12px',
                          padding: '10px 12px',
                          backgroundColor: '#7B68EE10',
                          borderLeft: '3px solid #7B68EE',
                          borderRadius: '0 6px 6px 0',
                          fontSize: '13px',
                          color: colors.textSecondary,
                        }}>
                          <span style={{ color: '#7B68EE', fontWeight: 600 }}>AI Insight:</span> {step.aiInsight}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>

        {/* AI Recommendation */}
        <Card style={{
          background: `linear-gradient(135deg, ${colors.cardBackground}, #7B68EE10)`,
          border: '1px solid #7B68EE40',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>🤖</span>
              <h2 style={{ fontSize: '20px', color: colors.textPrimary }}>AI-Powered Recommendation</h2>
            </div>
            {!showAiAnalysis && (
              <Button onClick={() => setShowAiAnalysis(true)}>
                Generate AI Analysis
              </Button>
            )}
          </div>

          {showAiAnalysis && (
            aiLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #E0E0E0',
                  borderTop: '4px solid #7B68EE',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px',
                }} />
                <p style={{ color: colors.textSecondary }}>Generating AI analysis...</p>
              </div>
            ) : (
              <div style={{
                color: colors.textSecondary,
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
                fontFamily: 'Inter, sans-serif',
              }}>
                {aiAnalysis?.recommendation || 'Click "Generate AI Analysis" to get personalized expansion recommendations.'}
              </div>
            )
          )}
        </Card>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

// Seller List Component
const ExpansionPathwayList: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['expansion-sellers'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/expansion-pathway/sellers`)
      return data
    },
  })

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Expandly Expansion Pathway
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Select a seller to view their personalized expansion pathway and recommendations
          </p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #7B68EE',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading sellers...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {(data?.sellers || []).map((seller: {
              store_id: number
              store_name: string
              country: string
              readiness_score: number
              total_revenue: number
              markets_count: number
            }) => (
              <Card
                key={seller.store_id}
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => navigate(`/expansion-pathway/${seller.store_id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '4px' }}>
                      {seller.store_name}
                    </h3>
                    <p style={{ fontSize: '14px', color: colors.textSecondary }}>{seller.country}</p>
                  </div>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: `conic-gradient(#7B68EE ${seller.readiness_score * 3.6}deg, ${colors.border} 0deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: colors.cardBackground,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#7B68EE',
                    }}>{seller.readiness_score}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: colors.textSecondary }}>Revenue: <strong style={{ color: '#50C878' }}>${seller.total_revenue.toLocaleString()}</strong></span>
                  <span style={{ color: colors.textSecondary }}>Markets: <strong style={{ color: colors.textPrimary }}>{seller.markets_count}</strong></span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
